/**
 * One Form A field, rendered from the Gazette schema.
 *
 * Nothing here is hand-written from the form: the key, the clause letter, the
 * label, the type and whether it is required all come from
 * `FORM_A_SECTIONS`, so a field that is not in the Gazette cannot appear on
 * this screen and a field that is in it cannot be quietly dropped.
 */

import { bi, type Bilingual, type FormAField } from "@ksidc/ontology";
import { useLang } from "@ksidc/client";
import { formatRupees } from "../format.js";

export interface AreaOption {
  id: string;
  label: Bilingual;
  boardConstituted: boolean;
}

export interface FieldRendererProps {
  field: FormAField;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: Bilingual | null;
  /** Reference data for the one select whose options are not in the Gazette. */
  areaOptions?: AreaOption[];
}

const LAKH = 1_00_000;

const asString = (v: unknown): string =>
  v === undefined || v === null ? "" : typeof v === "string" ? v : String(v);

const asRows = (v: unknown): Record<string, string>[] =>
  Array.isArray(v) ? (v as Record<string, string>[]) : [];

export function FieldRenderer({
  field,
  value,
  onChange,
  error,
  areaOptions,
}: FieldRendererProps): JSX.Element {
  const { t } = useLang();
  const id = `f-${field.key.replace(/\./g, "-")}`;
  const errorId = `${id}-error`;
  const helpId = `${id}-help`;

  // A table and a yes/no pair are groups of controls, not one control, so they
  // take a legend rather than a label.
  const isGroup = field.type === "table" || (field.type === "boolean" && field.key !== "declaration.agreed");

  const heading = (
    <>
      {field.clause ? (
        <span className="clause" aria-label={t(bi(`Clause ${field.clause}`, `ഖണ്ഡിക ${field.clause}`))}>
          {field.clause}
        </span>
      ) : null}
      {t(field.label)}
      {field.required ? (
        <span className="required-mark" title={t(bi("Required", "നിർബന്ധം"))}>
          *
        </span>
      ) : null}
    </>
  );

  const body = (
    <>
      {field.help ? (
        <p className="field-help" id={helpId}>
          {t(field.help)}
        </p>
      ) : null}

      <Control
        field={field}
        id={id}
        value={value}
        onChange={onChange}
        describedBy={[field.help ? helpId : "", error ? errorId : ""].filter(Boolean).join(" ")}
        areaOptions={areaOptions ?? []}
        invalid={Boolean(error)}
      />

      {error ? (
        <p className="field-error" id={errorId} role="alert">
          {t(error)}
        </p>
      ) : null}
    </>
  );

  const className = ["field", error ? "field-has-error" : ""].filter(Boolean).join(" ");

  if (isGroup) {
    return (
      <fieldset className={className}>
        <legend className="field-label">{heading}</legend>
        {body}
      </fieldset>
    );
  }

  return (
    <div className={className}>
      <label className="field-label" htmlFor={id}>
        {heading}
      </label>
      {body}
    </div>
  );
}

function Control({
  field,
  id,
  value,
  onChange,
  describedBy,
  areaOptions,
  invalid,
}: {
  field: FormAField;
  id: string;
  value: unknown;
  onChange: (value: unknown) => void;
  describedBy: string;
  areaOptions: AreaOption[];
  invalid: boolean;
}): JSX.Element {
  const { t } = useLang();
  const common = {
    id,
    "aria-describedby": describedBy.length > 0 ? describedBy : undefined,
    "aria-invalid": invalid || undefined,
  };

  switch (field.type) {
    case "longtext":
      return (
        <textarea
          {...common}
          value={asString(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case "number":
      return (
        <input
          {...common}
          type="number"
          inputMode="numeric"
          value={asString(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case "money_lakh": {
      const raw = asString(value);
      const parsed = Number(raw.replace(/[^0-9.]/g, ""));
      const rupees = Number.isFinite(parsed) && raw.trim().length > 0 ? parsed * LAKH : null;
      return (
        <>
          <div className="suffix-row">
            <input
              {...common}
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={raw}
              onChange={(e) => onChange(e.target.value)}
            />
            <span className="suffix">{t(bi("lakh rupees", "ലക്ഷം രൂപ"))}</span>
          </div>
          {rupees !== null && rupees > 0 ? (
            <p className="field-help" style={{ marginTop: "0.3rem", marginBottom: 0 }}>
              {formatRupees(rupees)}
            </p>
          ) : null}
        </>
      );
    }

    case "date":
      return (
        <input
          {...common}
          type="date"
          value={asString(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case "email":
      return (
        <input
          {...common}
          type="email"
          autoComplete="email"
          value={asString(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case "phone":
      return (
        <input
          {...common}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={asString(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      );

    case "select": {
      // The one select whose options are reference data rather than Gazette text.
      if (field.key === "land.industrialAreaId") {
        const chosen = areaOptions.find((a) => a.id === asString(value));
        return (
          <>
            <select
              {...common}
              value={asString(value)}
              onChange={(e) => onChange(e.target.value)}
            >
              <option value="">{t(bi("Choose an area", "ഒരു മേഖല തിരഞ്ഞെടുക്കുക"))}</option>
              {areaOptions.map((a) => (
                <option key={a.id} value={a.id}>
                  {t(a.label)}
                </option>
              ))}
            </select>
            {areaOptions.length === 0 ? (
              <p className="field-help" style={{ marginBottom: 0 }}>
                {t(
                  bi(
                    "The list of notified industrial areas for this district has not been loaded into this system yet. Which areas carry a constituted board is still an open question with KSIDC.",
                    "എറണാകുളത്തെ വിജ്ഞാപിത വ്യവസായ മേഖലകളുടെ പട്ടിക ഈ സംവിധാനത്തിൽ ഇതുവരെ ചേർത്തിട്ടില്ല. ഏതൊക്കെ മേഖലകൾക്ക് ബോർഡ് രൂപീകരിച്ചിട്ടുണ്ട് എന്നത് കെ.എസ്.ഐ.ഡി.സിയോട് ചോദിച്ചിരിക്കുന്ന കാര്യമാണ്.",
                  ),
                )}
              </p>
            ) : null}
            {chosen && !chosen.boardConstituted ? (
              <p className="field-help" style={{ marginBottom: 0 }}>
                {t(
                  bi(
                    "No Board has been set up for this area yet, as far as this system knows. Your application may not be accepted until one is set up.",
                    "ഈ മേഖലയ്ക്ക് ഇതുവരെ ഒരു ബോർഡ് രൂപീകരിച്ചതായി ഞങ്ങളുടെ വിവരങ്ങളിലില്ല. അത് രേഖപ്പെടുത്തുന്നതുവരെ നിങ്ങളുടെ അപേക്ഷ സ്വീകരിക്കാനായെന്ന് വരില്ല.",
                  ),
                )}
              </p>
            ) : null}
          </>
        );
      }

      return (
        <select {...common} value={asString(value)} onChange={(e) => onChange(e.target.value)}>
          <option value="">{t(bi("Choose one", "ഒന്ന് തിരഞ്ഞെടുക്കുക"))}</option>
          {(field.options ?? []).map((o) => (
            <option key={o.en} value={o.en}>
              {t(o)}
            </option>
          ))}
        </select>
      );
    }

    case "boolean": {
      // The declaration is a single act of assent; everything else is a choice
      // between two answers and is shown as two answers.
      if (field.key === "declaration.agreed") {
        return (
          <label className="checkbox-row" htmlFor={id}>
            <input
              {...common}
              type="checkbox"
              checked={value === true}
              onChange={(e) => onChange(e.target.checked)}
            />
            <span>{t(field.label)}</span>
          </label>
        );
      }
      return (
        <div className="choice-row">
          <label className={["choice", value === true ? "choice-selected" : ""].join(" ")}>
            <input
              {...common}
              type="radio"
              name={id}
              checked={value === true}
              onChange={() => onChange(true)}
            />
            <span>{t(bi("Yes", "അതെ"))}</span>
          </label>
          <label className={["choice", value === false ? "choice-selected" : ""].join(" ")}>
            <input
              type="radio"
              name={id}
              checked={value === false}
              onChange={() => onChange(false)}
            />
            <span>{t(bi("No", "അല്ല"))}</span>
          </label>
        </div>
      );
    }

    case "table":
      return <TableControl field={field} value={value} onChange={onChange} />;

    default:
      return (
        <input
          {...common}
          type="text"
          value={asString(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}

function TableControl({
  field,
  value,
  onChange,
}: {
  field: FormAField;
  value: unknown;
  onChange: (value: unknown) => void;
}): JSX.Element {
  const { t } = useLang();
  const rows = asRows(value);
  const columns = field.columns ?? [];

  const setCell = (index: number, key: string, cell: string): void => {
    const next = rows.map((r, i) => (i === index ? { ...r, [key]: cell } : r));
    onChange(next);
  };

  const addRow = (): void => {
    const blank: Record<string, string> = {};
    for (const c of columns) blank[c.key] = "";
    onChange([...rows, blank]);
  };

  const removeRow = (index: number): void => {
    onChange(rows.filter((_, i) => i !== index));
  };

  return (
    <div>
      {rows.length === 0 ? (
        <p className="field-help">
          {t(bi("Nothing entered yet.", "ഇതുവരെ ഒന്നും ചേർത്തിട്ടില്ല."))}
        </p>
      ) : (
        <div className="rows">
          {rows.map((row, index) => (
            <div className="row-card" key={`row-${index}`}>
              <div className="row-card-head">
                <strong>
                  {t(bi(`Entry ${index + 1}`, `${index + 1}-ാമത്തെ ഇനം`))}
                </strong>
                <button type="button" className="btn-quiet" onClick={() => removeRow(index)}>
                  {t(bi("Remove", "നീക്കം ചെയ്യുക"))}
                </button>
              </div>
              {columns.map((c) => (
                <div className="field" key={c.key}>
                  <label className="field-label" htmlFor={`t-${field.key}-${index}-${c.key}`}>
                    {t(c.label)}
                  </label>
                  <input
                    id={`t-${field.key}-${index}-${c.key}`}
                    type={c.type === "number" ? "number" : c.type === "date" ? "date" : "text"}
                    value={row[c.key] ?? ""}
                    onChange={(e) => setCell(index, c.key, e.target.value)}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      <button
        type="button"
        className="btn btn-secondary"
        style={{ marginTop: "0.7rem" }}
        onClick={addRow}
      >
        {t(bi("Add an entry", "ഒരു ഇനം ചേർക്കുക"))}
      </button>
    </div>
  );
}
